/* Video Carousel — dependency-free infinite video slider.
   - Center tile autoplays (muted, looping); side tiles show a poster only.
   - Videos are lazy: nothing loads until the carousel scrolls into view, and
     only the centered clip is ever loaded (its src is set on demand and cleared
     when it leaves the center) so it never affects initial page load.
   - Infinite loop via circular offset math (no clones, no libraries).
   - Keyboard: Tab to the arrows/controls, use them or Left/Right arrow keys to
     move; each slide change is announced via a polite live region. */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class VideoCarousel extends HTMLElement {
  #io = null;
  #ro = null;
  #current = 0;
  #step = 0;
  #inView = false;
  #soundOn = false;
  #visibleRange = 2;

  connectedCallback() {
    // One-time setup: cache nodes and bind listeners only once per element.
    if (this.dataset.ready !== 'true') {
      this.tiles = Array.from(this.querySelectorAll('[data-vcar-tile]'));
      this.count = this.tiles.length;
      if (this.count === 0) return;

      this.prevBtn = this.querySelector('[data-vcar-prev]');
      this.nextBtn = this.querySelector('[data-vcar-next]');
      this.status = this.querySelector('[data-vcar-status]');
      this.autoplay = this.dataset.autoplay !== 'false';
      this.#current = this.#wrapIndex(Number(this.dataset.start) || 0);

      this.#bindEvents();
      this.dataset.ready = 'true';
    }
    if (!this.count) return;

    this.#measure();
    this.#layout(false);

    // Observers are (re)created on every connect so a DOM move (e.g. theme
    // editor reorder) doesn't leave lazy-load / autoplay permanently dead.
    this.#io = new IntersectionObserver(this.#onIntersect.bind(this), {
      rootMargin: '200px 0px',
      threshold: 0.01,
    });
    this.#io.observe(this);

    if ('ResizeObserver' in window) {
      this.#ro = new ResizeObserver(() => {
        this.#measure();
        this.#layout(false);
      });
      this.#ro.observe(this);
    }
  }

  disconnectedCallback() {
    this.#io?.disconnect();
    this.#io = null;
    this.#ro?.disconnect();
    this.#ro = null;
    this.#pauseAll();
  }

  /** Public API — jump to a given tile index. */
  setCurrent(index) {
    const next = this.#wrapIndex(index);
    if (next === this.#current) return;
    this.#current = next;
    this.#layout(true);
  }

  #wrapIndex(n) {
    return ((n % this.count) + this.count) % this.count;
  }

  /** Shortest circular distance from the current center, in [-count/2, count/2]. */
  #wrappedOffset(i) {
    let offset = i - this.#current;
    const half = this.count / 2;
    if (offset > half) offset -= this.count;
    else if (offset < -half) offset += this.count;
    return offset;
  }

  #bindEvents() {
    this.prevBtn?.addEventListener('click', () => this.setCurrent(this.#current - 1));
    this.nextBtn?.addEventListener('click', () => this.setCurrent(this.#current + 1));

    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.setCurrent(this.#current - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.setCurrent(this.#current + 1);
      }
    });

    for (const [i, tile] of this.tiles.entries()) {
      // Mouse convenience: click a side tile to bring it to the center.
      tile.addEventListener('click', (event) => this.#onTileClick(event, i));

      const video = tile.querySelector('[data-vcar-video]');
      const play = tile.querySelector('[data-vcar-play]');
      video?.addEventListener('playing', () => {
        tile.classList.add('vcar__tile--playing');
        tile.classList.remove('vcar__tile--paused');
        play?.setAttribute('aria-label', 'Pause video');
      });
      video?.addEventListener('pause', () => {
        tile.classList.remove('vcar__tile--playing');
        if (i === this.#current) tile.classList.add('vcar__tile--paused');
        play?.setAttribute('aria-label', 'Play video');
      });

      const mute = tile.querySelector('[data-vcar-mute]');
      mute?.addEventListener('click', (event) => {
        event.stopPropagation();
        this.#toggleSound();
      });
    }
  }

  #onTileClick(event, i) {
    if (event.target.closest('[data-vcar-mute]')) return;
    if (i === this.#current) {
      this.#togglePlay();
    } else {
      this.setCurrent(i);
    }
  }

  #measure() {
    const styles = getComputedStyle(this);
    const tileWidth =
      this.tiles[0]?.offsetWidth ||
      parseFloat(styles.getPropertyValue('--vcar-tile-w')) ||
      300;
    const gap = parseFloat(styles.getPropertyValue('--vcar-gap')) || 16;
    this.#step = tileWidth + gap;
  }

  #layout(animate) {
    const useAnim = animate && !REDUCED_MOTION.matches;
    this.classList.toggle('vcar--anim', useAnim);

    for (const [i, tile] of this.tiles.entries()) {
      const offset = this.#wrappedOffset(i);
      const isCenter = offset === 0;
      const isVisible = Math.abs(offset) <= this.#visibleRange;

      tile.style.setProperty('--vcar-x', `${offset * this.#step}px`);
      tile.hidden = !isVisible;
      tile.classList.toggle('vcar__tile--center', isCenter);
    }

    if (this.status) {
      this.status.textContent = `Video ${this.#current + 1} of ${this.count}`;
    }

    this.#updatePlayback();
  }

  #updatePlayback() {
    for (const [i, tile] of this.tiles.entries()) {
      if (i !== this.#current) this.#unload(tile);
    }
    // Only load/play the centered clip, and only while genuinely on screen.
    if (this.autoplay && this.#inView) {
      this.#activate(this.tiles[this.#current]);
    }
  }

  #activate(tile) {
    const video = tile?.querySelector('[data-vcar-video]');
    if (!video) return;

    if (!video.getAttribute('src') && video.dataset.src) {
      video.setAttribute('src', video.dataset.src);
    }
    video.muted = !this.#soundOn;
    tile.classList.remove('vcar__tile--paused');

    // Keep this tile's mute button in sync with the shared sound state.
    const mute = tile.querySelector('[data-vcar-mute]');
    if (mute) {
      mute.setAttribute('aria-pressed', String(this.#soundOn));
      mute.setAttribute('aria-label', this.#soundOn ? 'Mute video' : 'Unmute video');
    }

    const played = video.play();
    if (played && typeof played.catch === 'function') {
      played.catch(() => tile.classList.add('vcar__tile--paused'));
    }
  }

  #unload(tile) {
    const video = tile.querySelector('[data-vcar-video]');
    if (!video) return;
    if (!video.paused) video.pause();
    tile.classList.remove('vcar__tile--playing', 'vcar__tile--paused');
    // Drop the source so only the centered clip stays loaded.
    if (video.getAttribute('src')) {
      video.removeAttribute('src');
      video.load();
    }
  }

  #togglePlay() {
    const video = this.tiles[this.#current]?.querySelector('[data-vcar-video]');
    if (!video) return;
    if (video.paused) this.#activate(this.tiles[this.#current]);
    else video.pause();
  }

  #toggleSound() {
    this.#soundOn = !this.#soundOn;
    for (const tile of this.tiles) {
      const mute = tile.querySelector('[data-vcar-mute]');
      if (mute) {
        mute.setAttribute('aria-pressed', String(this.#soundOn));
        mute.setAttribute('aria-label', this.#soundOn ? 'Mute video' : 'Unmute video');
      }
    }
    const video = this.tiles[this.#current]?.querySelector('[data-vcar-video]');
    if (video) video.muted = !this.#soundOn;
  }

  #pauseAll() {
    for (const tile of this.tiles) {
      const video = tile.querySelector('[data-vcar-video]');
      if (video && !video.paused) video.pause();
    }
  }

  #onIntersect(entries) {
    for (const entry of entries) {
      this.#inView = entry.isIntersecting;
      if (entry.isIntersecting) {
        if (this.autoplay) this.#activate(this.tiles[this.#current]);
      } else {
        this.#pauseAll();
      }
    }
  }
}

if (!customElements.get('video-carousel')) {
  customElements.define('video-carousel', VideoCarousel);
}
